import { baseApi } from "./baseApi";
import { CreateUserPayload } from "../../pages/UserManagement/components/CreateEditUserDrawer/CreateUserDrawer/utils/types";

interface UpdateSpecificBreakPayload {
  userId: string;
  breakId: string;
  start_date: string;
  end_date: string;
  is_deleted?: boolean;
}

interface UpdateAgentBreaksPayload {
  userId: string;
  start_date: string;
  end_date: string;
  is_deleted?: boolean;
}

interface GetUsersPayload {
  current_page: number;
  limit: number;
  clientCode?: string | null;
  role?: string | string[] | null;
  client_codes?: string | string[] | null;
  sort_by?: {
    field: string | null;
    order: number | null;
  };
  status?: string | string[] | null;
  is_org_request?: boolean;
}

export const userManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsersStatistics: builder.query({
      query: (clientCode?: string | null) => ({
        url: `/users/v2/statistics?${
          clientCode ? "is_org_request=true" : "is_org_request=false"
        }`,
        method: "GET",
      }),
      providesTags: [{ type: "User", id: "CreateUser" }],
    }),
    getUsers: builder.query({
      query: (payload: GetUsersPayload) => ({
        url: "/users/v2",
        method: "POST",
        body: {
          ...(payload?.sort_by && { sort_by: payload?.sort_by }),
          filter: {
            ...(payload?.clientCode && { org_code: payload?.clientCode }),
            ...(payload?.role && { role: payload?.role }),
            ...(payload?.status && { status: payload?.status }),
            ...(payload?.client_codes && {
              client_codes: payload?.client_codes,
            }),
            is_org_request: payload?.is_org_request,
          },
          pagination: {
            current_page: payload?.current_page,
            limit: payload?.limit,
          },
        },
      }),
      providesTags: [
        { type: "User", id: "CreateUser" },
        { type: "User", id: "MarkInactive" },
        { type: "User", id: "MarkActive" },
        { type: "User", id: "DeleteUser" },
      ],
    }),
    getRoleDetails: builder.query({
      query: () => ({
        url: "/users/v2/distribution-by-role",
        method: "GET",
      }),
    }),
    getCurrentClients: builder.query({
      query: () => ({
        url: "/users/clients",
        method: "GET",
      }),
    }),
    createUser: builder.mutation({
      query: (payload: CreateUserPayload) => ({
        url: "/auth/register",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "User", id: "CreateUser" }] : [],
    }),
    uploadUsers: builder.mutation({
      query: (formData: FormData) => ({
        url: "/users/v2/upload-users",
        method: "POST",
        body: formData,
        formData: true,
      }),
    }),
    createUsersFromFile: builder.mutation({
      query: (payload: { key: string }) => ({
        url: "/auth/v2/create-users-from-file",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "User", id: "CreateUser" },
        { type: "User", id: "MarkInactive" },
        { type: "User", id: "MarkActive" },
      ],
    }),
    downloadSampleCsv: builder.query<Blob, void>({
      query: () => ({
        url: "/users/v2/download-sample-csv",
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    bulkMarkUsersInactiveValidation: builder.mutation({
      query: (payload: { user_ids: string[] }) => ({
        url: "/users/v2/mark-as-inactive/validate",
        method: "POST",
        body: payload,
      }),
    }),
    bulkMarkUsersInactive: builder.mutation({
      query: (payload: { user_ids: string[] }) => ({
        url: "/users/v2/mark-as-inactive",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: [{ type: "User", id: "MarkInactive" }],
    }),
    resendInvite: builder.mutation({
      query: (payload: string) => ({
        url: "/auth/resend_creation_email",
        params: { email: payload },
        method: "POST",
      }),
      invalidatesTags: [{ type: "User", id: "CreateUser" }],
    }),
    updateUserDetails: builder.mutation({
      query: (payload: { _id: string; formData: FormData }) => ({
        url: `/users/${payload._id}`,
        method: "PUT",
        body: payload.formData,
      }),
      invalidatesTags: [{ type: "User", id: "MarkActive" }],
    }),
    getUserDetailsById: builder.query({
      query: (userId) => ({
        url: `/users/get-details/${userId}`,
        method: "GET",
      }),
    }),
    searchUsers: builder.query({
      query: (payload: {
        searchQuery: string;
        skip: number;
        limit: number;
        is_org_request?: boolean;
      }) => ({
        url: `/users/v2/search-users`,
        method: "GET",
        params: {
          query: payload.searchQuery,
          skip: payload.skip,
          limit: payload.limit,
          ...(payload.is_org_request && { is_org_request: true }),
        },
      }),
    }),
    getValidRolesForUserCreation: builder.query({
      query: () => ({
        url: "/users/v2/valid-roles-for-user-creation",
        method: "GET",
      }),
    }),
    getAgentBreaks: builder.query({
      query: (userId: string) => ({
        url: `/users/${userId}/agent-leaves`,
        method: "GET",
      }),
      providesTags: [{ type: "User", id: "AgentBreaks" }],
    }),
    updateAgentBreaks: builder.mutation({
      query: ({
        userId,
        start_date,
        end_date,
        is_deleted,
      }: UpdateAgentBreaksPayload) => ({
        url: `/users/${userId}/agent-leaves`,
        method: "POST",
        body: { start_date, end_date, is_deleted },
      }),
      invalidatesTags: [{ type: "User", id: "AgentBreaks" }],
    }),
    updateSpecificAgentBreak: builder.mutation({
      query: ({
        userId,
        breakId,
        start_date,
        end_date,
        is_deleted,
      }: UpdateSpecificBreakPayload) => ({
        url: `/users/${userId}/agent-leaves/${breakId}`,
        method: "PATCH",
        body: { start_date, end_date, is_deleted },
      }),
      invalidatesTags: [{ type: "User", id: "AgentBreaks" }],
    }),
    getAllAgents: builder.query({
      query: (leadId?: string | void) => ({
        url: `/users/get-agents${leadId ? `?lead_id=${leadId}` : ""}`,
        method: "GET",
      }),
    }),
    deleteUser: builder.mutation({
      query: (email: string) => ({
        url: `/users/onboarding/${encodeURIComponent(email)}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "User", id: "DeleteUser" }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAllUsersStatisticsQuery,
  useGetUsersQuery,
  useGetRoleDetailsQuery,
  useLazyGetRoleDetailsQuery,
  useGetCurrentClientsQuery,
  useCreateUserMutation,
  useUploadUsersMutation,
  useCreateUsersFromFileMutation,
  useLazyDownloadSampleCsvQuery,
  useBulkMarkUsersInactiveValidationMutation,
  useBulkMarkUsersInactiveMutation,
  useResendInviteMutation,
  useUpdateUserDetailsMutation,
  useGetUserDetailsByIdQuery,
  useLazySearchUsersQuery,
  useGetValidRolesForUserCreationQuery,
  useGetAgentBreaksQuery,
  useUpdateAgentBreaksMutation,
  useUpdateSpecificAgentBreakMutation,
  useGetAllAgentsQuery,
  useDeleteUserMutation,
} = userManagementApi;
